import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSurveyTemplates,
  getSurveyTemplateDetails,
  createSurveyTemplate,
  updateSurveyTemplate,
  deleteSurveyTemplate,
} from "api/bSurveysApi";

/**
 * Hook for managing survey templates CRUD operations
 */
export function useSurveyTemplates(organisationId) {
  const queryClient = useQueryClient();

  const { data: surveyTemplates, isLoading, error, refetch } = useQuery({
    queryKey: ["surveyTemplates", organisationId],
    queryFn: () => getSurveyTemplates(organisationId).then((res) => res.data),
    enabled: !!organisationId,
  });

  const createMutation = useMutation({
    mutationFn: createSurveyTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(["surveyTemplates", organisationId]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ templateId, data }) => updateSurveyTemplate(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["surveyTemplates", organisationId]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSurveyTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(["surveyTemplates", organisationId]);
    },
  });

  return {
    surveyTemplates,
    isLoading,
    error,
    refetch,
    createTemplate: createMutation.mutate,
    createTemplateAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
    updateTemplate: updateMutation.mutate,
    updateTemplateAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    deleteTemplate: deleteMutation.mutate,
    deleteTemplateAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
  };
}

/**
 * Hook for fetching a single survey template
 */
export function useSurveyTemplateDetails(templateId) {
  return useQuery({
    queryKey: ["surveyTemplate", templateId],
    queryFn: () => getSurveyTemplateDetails(templateId).then((res) => res.data),
    enabled: !!templateId && templateId !== 'new',
  });
}
